import { useWatch } from 'react-hook-form';

import {
	PROGRAM_SECTIONS,
	programOwnerPreorderDtoSchema,
	type ProgramOwnerPreorderInputDto,
	type ProgramOwnerServiceConfig,
	Services,
	type ServicesProvider,
	SETTINGS_GROUPS,
} from '@ekhodealer/ekho-common';
import { Box, Form, Group, Stack, Text } from '@ekhodealer/ekho-common/rev';

import type {
	ProgramOwnerPreorderOutputDto} from '@ekhodealer/ekho-common';

import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';

import { useUpdatePreorder } from '../../../api/configuration/services/useUpdatePreorder';
import { FormSection } from '../../config-mgmt/FormSection';
import { ParentSection } from '../../config-mgmt/ParentSection';

/**
 * Helper function to get initial form data for preorder
 */
function getDefaultValues(
	servicesProvider: ServicesProvider | undefined | null,
): ProgramOwnerPreorderOutputDto {
	const programOwnerConfig = servicesProvider?.config?.[Services.PROGRAM_OWNER] as
		| ProgramOwnerServiceConfig
		| undefined;
	const preorderConfig = programOwnerConfig?.preorder;

	return {
		enabled: preorderConfig?.enabled ?? false,
		preorderPaymentConfig: {
			PREORDER_RESERVATION: {
				paymentTypeEnum: 'PREORDER_RESERVATION',
				amount: preorderConfig?.preorderPaymentConfig?.PREORDER_RESERVATION?.amount ?? 0,
				title: preorderConfig?.preorderPaymentConfig?.PREORDER_RESERVATION?.title ?? '',
				body: preorderConfig?.preorderPaymentConfig?.PREORDER_RESERVATION?.body ?? '',
			},
			PREORDER_VIP_RESERVATION: preorderConfig?.preorderPaymentConfig
				?.PREORDER_VIP_RESERVATION
				? {
						paymentTypeEnum: 'PREORDER_VIP_RESERVATION',
						amount:
							preorderConfig.preorderPaymentConfig.PREORDER_VIP_RESERVATION.amount ??
							0,
						title:
							preorderConfig.preorderPaymentConfig.PREORDER_VIP_RESERVATION.title ??
							'',
						body:
							preorderConfig.preorderPaymentConfig.PREORDER_VIP_RESERVATION.body ??
							'',
				  }
				: undefined,
		},
		preorderCollectShippingAddress: preorderConfig?.preorderCollectShippingAddress ?? false,
		preorderProdStart: preorderConfig?.preorderProdStart ?? '',
	};
}

export function ProgramOwnerPreorderSection() {
	const { data: servicesProvider } = useCurrentServicesProvider();
	const updatePreorder = useUpdatePreorder();

	const handleSubmit = async (
		data: ProgramOwnerPreorderInputDto,
	): Promise<{ isSuccess: boolean }> => {
		try {
			const cleanedData = { ...data };
			if (!cleanedData.preorderProdStart) {
				cleanedData.preorderProdStart = undefined;
			}
			await updatePreorder.mutateAsync(cleanedData);
			return { isSuccess: true };
		} catch (error) {
			console.error('Failed to update preorder:', error);
			return { isSuccess: false };
		}
	};

	return (
		<ParentSection
			sectionPath={{
				group: SETTINGS_GROUPS.PROGRAM,
				section: PROGRAM_SECTIONS.PREORDER,
			}}
			title='Preorder'
			description="Allow buyers to preorder vehicles before they're available with refundable deposits. Best for pre-launch or out of stock models.">
			<FormSection
				sectionPath={{
					group: SETTINGS_GROUPS.PROGRAM,
					section: PROGRAM_SECTIONS.PREORDER,
				}}
				form={{
					schema: programOwnerPreorderDtoSchema,
					defaultValues: getDefaultValues(servicesProvider),
					onSubmit: handleSubmit,
				}}
				title='Standard preorder'
				description='Enable standard preorders to collect refundable deposits from buyers for models that are not yet available.'
				formTitle='Preorder configuration'
				toggleSettings={{
					fieldName: 'enabled',
					visibility: 'write',
				}}
				hideDivider>
				<PreorderFormFields />
			</FormSection>
		</ParentSection>
	);
}

/**
 * Component that renders the form fields and watches VIP toggle for conditional fields
 */
function PreorderFormFields() {
	const vipReservation = useWatch({
		name: 'preorderPaymentConfig.PREORDER_VIP_RESERVATION',
	});
	const hasVip = !!vipReservation;

	return (
		<Stack itemsSpacing='24'>
			{/* Standard Preorder Section */}
			<Stack>
				<Group itemsSpacing='16'>
					<Group.Item grow={1}>
						<Form.Field.Text
							schemaField='preorderPaymentConfig.PREORDER_RESERVATION.amount'
							labelText='Deposit amount'
							required
							type='price'
							size='large'
							placeholder='e.g. $1000'
						/>
					</Group.Item>
					<Group.Item grow={1}>
						<Form.Field.Text
							schemaField='preorderPaymentConfig.PREORDER_RESERVATION.title'
							labelText='Title'
							descriptionText='Name of preorder option shown to buyers, defaults to "Reserve" if left blank'
							size='large'
							placeholder='E.g. "Reserve Now"'
						/>
					</Group.Item>
				</Group>
				<Form.Field.TextArea
					schemaField='preorderPaymentConfig.PREORDER_RESERVATION.body'
					labelText='Description'
					size='large'
					placeholder='e.g. "Reserve now to secure your spot and receive priority fulfillment."'
					rows={2}
				/>
			</Stack>

			{/* VIP Preorder Section */}
			<Box padding='20' background='secondary'>
				<Stack itemsSpacing='16'>
					<Form.Field.Switch
						schemaField='preorderPaymentConfig.PREORDER_VIP_RESERVATION'
						labelText='VIP preorder'
						descriptionText='Enable a premium preorder option. When enabled, buyers choose between standard and VIP preorders. Typically, a higher preorder deposit in exchange for higher priority fulfillment.'
						level='subsection'
						titleWeight='semibold'
						togglePosition='right'
						objectValue={{
							paymentTypeEnum: 'PREORDER_VIP_RESERVATION',
							amount: 0,
							title: '',
							body: '',
						}}
					/>
					{hasVip && (
						<Stack>
							<Group itemsSpacing='16'>
								<Group.Item grow={1}>
									<Form.Field.Text
										schemaField='preorderPaymentConfig.PREORDER_VIP_RESERVATION.amount'
										labelText='Deposit amount'
										descriptionText='Typically higher than standard preorder deposit'
										required
										type='price'
										size='large'
										variant='filled'
										placeholder='e.g. $1000'
									/>
								</Group.Item>
								<Group.Item grow={1}>
									<Form.Field.Text
										schemaField='preorderPaymentConfig.PREORDER_VIP_RESERVATION.title'
										labelText='Title'
										descriptionText='name of VIP preorder option shown to buyers, defaults to "Reserve (VIP)" if left blank'
										required
										size='large'
										variant='filled'
										placeholder='E.g. "VIP preorder"'
									/>
								</Group.Item>
							</Group>
							<Form.Field.TextArea
								schemaField='preorderPaymentConfig.PREORDER_VIP_RESERVATION.body'
								labelText='Description'
								descriptionText='VIP preorder benefits shown to buyers, such as early delivery, free shipping, or included accessories.'
								variant='filled'
								required
								rows={2}
							/>
						</Stack>
					)}
				</Stack>
			</Box>

			{/* Additional Settings Section */}
			<Box padding='20' background='secondary'>
				<Stack itemsSpacing='24'>
					<Text size='heading' weight='semibold'>
						Additional settings
					</Text>
					<Form.Field.Switch
						schemaField='preorderCollectShippingAddress'
						labelText='Collect shipping address for preorders'
						descriptionText='When enabled, buyers’ shipping addresses are collected during preorder. Useful for tracking regional demand.'
						togglePosition='left'
					/>

					<Form.Field.Text
						schemaField='preorderProdStart'
						labelText='Estimated fulfillment time'
						descriptionText='Text shown to buyers indicating when they can expect to receive their vehicle (e.g. "Fall 2026").'
						size='large'
						variant='filled'
						placeholder='E.g. Fall 2026'
						showFieldToggle={true}
					/>
				</Stack>
			</Box>
		</Stack>
	);
}

export default ProgramOwnerPreorderSection;
